// src/contexts/AuthContext.jsx - Apartment Management System
import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { setSubscriptionRequiredHandler } from '../services/api';
import { getAuthToken, getCurrentUser, isAuthenticated as checkAuthentication, isTokenExpired } from '../models/authModel';
import { registerPushToken, unregisterPushToken } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);

  useEffect(() => {
    // Register the subscription required handler
    setSubscriptionRequiredHandler(() => {
      setIsSubscriptionModalOpen(true);
    });

    const initAuth = async () => {
      try {
        const token = getAuthToken();
        const userData = getCurrentUser();

        if (token && userData) {
          // Serve cached local data first for instantly fast rendering
          setUser(userData);
          setIsAuthenticated(true);

          // Only refresh the admin/staff profile — portal users (tenant/owner/vendor) use
          // a separate auth system or specific portal endpoints.
          const isPortalUser = userData.role === 'tenant' || userData.role === 'owner' || userData.role === 'vendor';

          if (!isPortalUser) {
            // Silently fetch fresh admin/staff profile data in the background
            try {
              const response = await api.get('/users/profile');
              if (response.data && response.data.success && response.data.user) {
                // Update context and local storage with fresh data (e.g., subscription changes)
                const freshUser = { ...userData, ...response.data.user };
                setUser(freshUser);
                localStorage.setItem('user', JSON.stringify(freshUser)); // user profile (non-sensitive)
              }
            } catch (profileErr) {
              // 401 is already handled by the api interceptor (refresh → retry → logout if refresh fails).
              // 403 non-subscription errors are also toasted by the interceptor.
              // Nothing to do here except log — avoid double-logout race.
              if (profileErr.response?.status !== 401) {
                console.error('Failed to fetch fresh profile background:', profileErr);
              }
            }
          }

        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // User data initialization is handled in first useEffect

  // Check token expiration every 5 minutes — access tokens are 8h so no need to poll aggressively
  useEffect(() => {
    const checkTokenInterval = setInterval(() => {
      if (isAuthenticated && isTokenExpired()) {
        logout();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(checkTokenInterval);
  }, [isAuthenticated]);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);

    const isPortalUser = userData.role === 'tenant' || userData.role === 'owner' || userData.role === 'vendor';
    if (!isPortalUser) {
      api.get('/users/profile').then(response => {
        if (response.data?.success && response.data?.user) {
          const freshUser = { ...userData, ...response.data.user };
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        }
      }).catch(() => {});
      // Register FCM push token for admin/staff
      registerPushToken(api, '/push/token');
    }
  };

  const logout = async () => {
    const isPortalUser = user?.role === 'tenant' || user?.role === 'owner' || user?.role === 'vendor';
    if (!isPortalUser) unregisterPushToken(api, '/push/token');
    // Revoke refresh token server-side and clear the httpOnly cookie
    try { await api.post('/auth/logout'); } catch { /* best-effort */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setIsSubscriptionModalOpen(false);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const hasFeature = (feature) => {
    if (!user) return false;
    // Portal users (tenant/owner/vendor) bypass all subscription feature checks —
    // their access is controlled by PortalProtectedRoute, not subscription tiers.
    if (user.role === 'tenant' || user.role === 'owner' || user.role === 'vendor') return true;
    const tier = user.subscriptionTier?.toLowerCase() || 'starter';

    // Simple feature mapping
    const GROWTH_FEATURES = [
      'basic_reports', 'id_documents', 'whatsapp', 'analytics', 'advanced_reports',
      'templates', 'inspections', 'contracts', 'leases', 'maintenance', 'owner_mgmt',
      'bookings', 'transfers', 'portals', 'e_signatures', 'advanced_logs', 'utilities',
      'amenities', 'screening', 'violations', 'evictions', 'pro_tools'
    ];
    const PRO_FEATURES = [
      ...GROWTH_FEATURES,
      'ai_features', 'gl_accounting', 'api_access', 'stripe_connect', 'otp_2fa',
      'multi_org', 'custom_reports', 'investment_dashboard', 'webhooks'
    ];
    const featuresByTier = {
      'starter': ['basic_reports', 'id_documents'],
      'growth': GROWTH_FEATURES,
      'pro': PRO_FEATURES,
      'enterprise': PRO_FEATURES
    };

    const allowedFeatures = featuresByTier[tier] || featuresByTier['starter'];
    return allowedFeatures.includes(feature);
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    isSubscriptionModalOpen,
    setIsSubscriptionModalOpen,
    login,
    logout,
    updateUser,
    hasFeature
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;